import { api } from "./api";

export type PlatformCategory = "ota" | "finance" | "crawl";
export type PlatformStatus = "connected" | "disconnected" | "error" | "pending";
export type SyncLogStatus = "ok" | "partial" | "failed" | "running";
export type CrawlJobStatus = "queued" | "running" | "done" | "failed";
export type MappingType = "sku" | "price" | "field";

export interface Platform {
  id: string;
  category: PlatformCategory;
  name: string;
  slug: string;
  status: PlatformStatus;
  last_sync_at: string | null;
  error_count: number;
  config_summary: Record<string, unknown>;
  enabled_apis: string[];      // W6: 6 类 API 开关
  sync_stats: {
    total_synced: number;
    last_7d: number;
    errors_7d: number;
  };
}

export interface PlatformUpdate {
  api_key?: string;
  enabled_apis?: string[];
  webhook_url?: string;
}

export interface TestConnectionResult {
  platform_id: string;
  platform_name: string;
  status: "not_configured" | "connected" | "error";
  latency_ms: number | null;
  message: string;
  hint: string;
}

export interface SyncLog {
  id: string;
  platform_id: string;
  platform_name: string;
  direction: "inbound" | "outbound";
  resource: string;
  started_at: string;
  finished_at: string | null;
  status: SyncLogStatus;
  items_count: number;
  error_message: string | null;
}

export interface ErrorQueueItem {
  id: string;
  platform_id: string;
  platform_name: string;
  occurred_at: string;
  operation: string;
  error_class: string;
  error_message: string;
  retry_count: number;
  last_retry_at: string | null;
}

export interface CrawlJob {
  id: string;
  platform_id: string;
  platform_name: string;
  job_type: string;
  status: CrawlJobStatus;
  scheduled_at: string;
  started_at: string | null;
  finished_at: string | null;
  items_scraped: number;
  note: string | null;
}

export interface Mapping {
  id: string;
  platform_id: string;
  platform_name: string;
  mapping_type: MappingType;
  field_local: string;
  field_remote: string;
  transform: string | null;
  enabled: boolean;
}

// W6: 6 类业务 API
export const API_TYPES: Record<string, string> = {
  inventory: "库存同步",
  pricing: "价格同步",
  order: "订单推送",
  voucher: "凭证推送",
  settlement: "结算同步",
  crawl: "数据抓取",
};

export const PLATFORM_STATUS_LABEL: Record<PlatformStatus, string> = {
  connected: "已连接",
  disconnected: "未连接",
  error: "连接错误",
  pending: "待启用",
};

export const SYNC_STATUS_LABEL: Record<SyncLogStatus, string> = {
  ok: "成功",
  partial: "部分",
  failed: "失败",
  running: "运行中",
};

export const CRAWL_STATUS_LABEL: Record<CrawlJobStatus, string> = {
  queued: "排队中",
  running: "运行中",
  done: "完成",
  failed: "失败",
};

export const integrationApi = {
  listPlatforms: () => api<Platform[]>("/api/v1/integration/platforms"),
  getPlatform: (id: string) => api<Platform>(`/api/v1/integration/platforms/${id}`),
  updatePlatform: (id: string, payload: PlatformUpdate) =>
    api<Platform>(`/api/v1/integration/platforms/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  testConnection: (platform_id: string) =>
    api<TestConnectionResult>("/api/v1/integration/_test-connection", {
      method: "POST",
      body: JSON.stringify({ platform_id }),
    }),
  syncLogs: (platform_id?: string, status?: SyncLogStatus) =>
    api<SyncLog[]>(
      `/api/v1/integration/sync-logs${
        platform_id || status
          ? `?${new URLSearchParams({
              ...(platform_id ? { platform_id } : {}),
              ...(status ? { status } : {}),
            }).toString()}`
          : ""
      }`,
    ),
  errorQueue: () => api<ErrorQueueItem[]>("/api/v1/integration/error-queue"),
  crawlJobs: () => api<CrawlJob[]>("/api/v1/integration/crawl-jobs"),
  mappings: () => api<Mapping[]>("/api/v1/integration/mappings"),
};
